import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CodeBlock } from '@/components/ui/code-block';
import type { AppDownload } from '../api';
import { MAC_UNINSTALL_COMMAND, browserPlatforms, formatBytes, macInstallCommand } from '../format';
import { useAppDownloads } from '../hooks';
import { Card } from './Facts';

const PLATFORMS: Record<string, string> = {
  'linux-amd64': 'Ubuntu and Debian',
  'rpm-x86_64': 'Fedora and RHEL',
  'windows-x86_64': 'Windows',
  'macos-universal': 'macOS',
};

function platformName(platform: string): string {
  return PLATFORMS[platform] ?? platform;
}

/** What to do after downloading, where it isn't just "open it". */
function InstallHint({ download }: { download: AppDownload }) {
  if (download.platform === 'linux-amd64') {
    return (
      <p className="text-xs text-muted-foreground">
        Then, from the folder it&rsquo;s in: <code className="font-mono">sudo apt install ./{download.file}</code>
      </p>
    );
  }
  if (download.platform === 'rpm-x86_64') {
    return (
      <p className="text-xs text-muted-foreground">
        Then, from the folder it&rsquo;s in: <code className="font-mono">sudo dnf install ./{download.file}</code>. On
        RHEL 10 and its rebuilds, turn on EPEL first.
      </p>
    );
  }
  if (download.platform === 'windows-x86_64') {
    return (
      <p className="text-xs text-muted-foreground">
        If Windows says the installer is from an unknown publisher, choose More info, then Run anyway.
      </p>
    );
  }
  return null;
}

/** The Mac's installer: the Terminal command first, since a package the
 *  browser downloaded is refused until the user lets it open. */
function MacInstall({ download }: { download: AppDownload }) {
  return (
    <div className="space-y-2">
      <p className="text-sm">In Terminal, this downloads and installs it, asking for your password:</p>
      <CodeBlock code={macInstallCommand(download.url)} />
      <p className="text-xs text-muted-foreground">
        Version {download.version} · {formatBytes(download.size)}
        {download.minimumOs ? ` · ${download.minimumOs} or later` : ''}
      </p>
      <Button asChild variant="outline" size="sm">
        <a href={download.url}>Or download the package</a>
      </Button>
      <p className="text-xs text-muted-foreground">
        macOS refuses a package downloaded in the browser at first: in System Settings, under Privacy &amp; Security,
        choose Open Anyway. To remove Witina VPN later: <code className="font-mono">{MAC_UNINSTALL_COMMAND}</code>
      </p>
    </div>
  );
}

/**
 * The Witina VPN app's installers, for the channel the caller's machines
 * follow (docs/VpnAppReleases.md, "Downloads for people"): this browser's
 * platform first, then the others. A browser that doesn't say which Linux
 * it's on gets both Linux packages first.
 */
export function AppDownloads() {
  const { data, isLoading, isError } = useAppDownloads();
  const mine = browserPlatforms();
  const downloads = data?.downloads ?? [];
  const primaries = mine.flatMap((platform) => downloads.filter((d) => d.platform === platform));
  const others = downloads.filter((d) => !primaries.includes(d));

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Download Witina VPN
          {data?.channel === 'beta' ? (
            <Badge variant="secondary" className="text-[10px]" title="Your organization gets beta versions">
              Beta
            </Badge>
          ) : null}
        </span>
      }
      description="Install it on the computer you connect to customer sites from."
    >
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="text-xs text-muted-foreground">Couldn&rsquo;t load the downloads.</p>
      ) : downloads.length === 0 ? (
        <p className="text-xs text-muted-foreground">No download is available yet.</p>
      ) : (
        <div className="space-y-4">
          {primaries.length > 1 ? (
            <p className="text-xs text-muted-foreground">This browser doesn&rsquo;t say which Linux it&rsquo;s on: choose yours.</p>
          ) : null}
          {primaries.map((primary) =>
            primary.platform === 'macos-universal' ? (
              <MacInstall key={primary.url} download={primary} />
            ) : (
              <div key={primary.url} className="space-y-1.5">
                <Button asChild>
                  <a href={primary.url}>Download for {platformName(primary.platform)}</a>
                </Button>
                <p className="text-xs text-muted-foreground">
                  Version {primary.version} · {formatBytes(primary.size)}
                  {primary.minimumOs ? ` · ${primary.minimumOs} or later` : ''}
                </p>
                <InstallHint download={primary} />
              </div>
            ),
          )}
          {primaries.length === 0 && mine.length > 0 ? (
            <p className="text-sm">Witina VPN for {mine.map(platformName).join(' or ')} is coming soon.</p>
          ) : null}
          {others.length > 0 ? (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">{primaries.length > 0 ? 'Also for' : 'Available for'}</p>
              <ul className="space-y-1">
                {others.map((d) => (
                  <li key={d.url} className="text-sm">
                    <a href={d.url} className="text-primary hover:underline">
                      {platformName(d.platform)}
                    </a>{' '}
                    <span className="text-xs text-muted-foreground">
                      {d.version} · {formatBytes(d.size)}
                      {d.minimumOs ? ` · ${d.minimumOs} or later` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}
